// ============================================================================
// useReturningTrainer — landing-only, optional-first session probe.
// ----------------------------------------------------------------------------
// The landing page is PUBLIC, so it must never depend on data being present.
// This hook taps the SAME typed API client + React Query cache the auth layer
// uses (qk.auth.me / authApi.me): if a signed vf_session cookie is still valid
// we get the trainer back and can warmly personalise the hero ("Welcome back");
// a 401 (or any error / network blip) resolves to null and the marketing page
// renders exactly as it would for a brand-new visitor.
//
// It shares the `qk.auth.me` key with AuthContext, so when AppShell's live-sync
// or the auth provider refreshes the current trainer, this stays in lockstep —
// no extra request, no divergent state. Strictly additive: the page works with
// or without it.
// ============================================================================
import { useQuery } from "@tanstack/react-query";
import { ApiError, authApi, qk } from "../../api";
import type { Trainer } from "../../api";

export interface ReturningTrainer {
  /** The signed-in trainer, or null when logged-out / unknown. */
  trainer: Trainer | null;
  /** First name only, for a friendly greeting. Empty when unknown. */
  firstName: string;
  /** True only once we positively know a valid session exists. */
  isReturning: boolean;
  /** Still resolving the initial probe. */
  isLoading: boolean;
}

export function useReturningTrainer(): ReturningTrainer {
  const query = useQuery<Trainer | null>({
    queryKey: qk.auth.me,
    queryFn: async ({ signal }) => {
      try {
        return await authApi.me(signal);
      } catch (err) {
        // Logged-out (or offline) is a perfectly normal state here.
        if (err instanceof ApiError) return null;
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const trainer = query.data ?? null;
  const firstName =
    (trainer?.display_name ?? trainer?.username ?? "").trim().split(/\s+/)[0] ??
    "";

  return {
    trainer,
    firstName,
    isReturning: !!trainer,
    isLoading: query.isLoading,
  };
}
